// ** React Imports

// ** Axios
import axios from 'axios'
import { useAtom } from 'jotai'
import { createContext, ReactNode, useEffect, useState } from 'react'
// ** Router
import { useLocation, useNavigate } from 'react-router-dom'
import { type CapabilityWithPage, capabilitiesAtom, selectedRoleAtom } from 'src/atoms'
import client from 'src/client'
// ** Config
import authConfig from 'src/configs/auth'
import type { GetCurrentUserResponse, Page } from 'src/types/directus/current-user'
import { currentSelectedRole, getPageCapabilities } from 'src/utils/general'
import { useAtomUserMe } from '@/hooks/stores/useAtomUserMe'
import { env } from '@/utils/env'
// ** Types
import type { AuthValuesType, ErrCallbackType, LoginParams, RegisterParams } from './types'

// ** Defaults
const defaultProvider: AuthValuesType = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
}

const AuthContext = createContext(defaultProvider)

type Props = {
  children: ReactNode
}

const AuthProvider = ({ children }: Props) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState<boolean>(defaultProvider.loading)
  const { setAtomUserMe } = useAtomUserMe()
  const [, setCapabilities] = useAtom(capabilitiesAtom)
  const [, setSelectedRole] = useAtom(selectedRoleAtom)

  // ** Hooks
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await client.api.get<GetCurrentUserResponse>('/api/user/me')
        const user = response.data.data
        if (!user) {
          setLoading(false)
          if (pathname !== '/login' && !pathname.startsWith('/public')) {
            navigate('/login')
          }
          return
        }

        setUser(user)
        window.localStorage.setItem('userData', JSON.stringify(user))

        const roles = user.roles.map(role => ({
          id: role.id,
          label: role.name,
          code: role.code
        }))

        const isSingleRole = roles.length === 1

        if (response.data.data && pathname === '/login') {
          setLoading(false)
          navigate('/')
          return
        }

        const superAdminRoleCodes = authConfig.superAdminRoleCodes

        setAtomUserMe({ data: response.data })

        const options = user.roles.map(role => ({
          ...role,
          id: role.id,
          label: role.name,
          code: role.code
        }))

        const pages = [
          ...(user.modules?.flatMap(module => module.clusters).flatMap(cluster => cluster.pages) || []),
          ...(user.general_pages || [])
        ]

        const capabilities = pages.flatMap(page => page.capabilities)

        const capabilityWithPages: CapabilityWithPage[] = []
        for (const capability of capabilities) {
          const relatedPage = pages.find(page => page.id === capability.page)
          if (!relatedPage) {
            continue
          }

          capabilityWithPages.push({
            ...capability,
            page: relatedPage
          })
        }

        setCapabilities(capabilityWithPages)

        try {
          const getPageStatus = await client.api.get<{ data: Page[] }>('/items/mt_pages', {
            params: {
              fields: 'status',
              filter: {
                url: {
                  _eq: pathname
                }
              }
            }
          })

          const selectedRole = options.find(role => role.id === (isSingleRole ? roles[0].id : currentSelectedRole?.id))
          const isSuperAdmin = selectedRole?.code && superAdminRoleCodes.includes(selectedRole.code)

          const pageCapabilities = getPageCapabilities({ capabilities: capabilityWithPages, pathname })
          const isPagePublished = getPageStatus.data.data && getPageStatus.data.data[0]?.status === 'published'

          const hasValidCapability = pageCapabilities.some(c => c.status === 'published')

          if (
            !isSuperAdmin &&
            (!hasValidCapability || !isPagePublished) &&
            pathname !== '/not-found' &&
            pathname !== '/' &&
            !pathname.startsWith('/public')
          ) {
            navigate('/not-found')
            return
          }
        } catch {
          if (pathname !== '/not-found' && pathname !== '/' && !pathname.startsWith('/public')) {
            navigate('/not-found')
            return
          }
        }

        setLoading(false)
      } catch {
        setLoading(false)
        if (pathname !== '/login' && !pathname.startsWith('/public')) {
          navigate('/login')
        }
      }
    }

    initAuth()
  }, [pathname, navigate, setCapabilities, setAtomUserMe, setSelectedRole])

  const handleLogin = async (params: LoginParams, errorCallback?: ErrCallbackType) => {
    try {
      const authService = env('NEXT_PUBLIC_AUTH_DEFAULT') || 'DATACORE'

      if (authService === 'DATACORE') {
        const body = {
          grant_type: 'password',
          client_id: 'client1',
          username: 'anang',
          password: 'Laniuslab123!'
        }

        await client.api.post('connect/token', body)
      } else if (authService === 'DIRECTUS') {
        const body = {
          email: params.email,
          isRemember: true,
          password: params.password
        }

        await client.api.post('auth/login', body)
      }

      const storedToken = window.localStorage.getItem(authConfig.accessTokenKeyName)!
      if (storedToken) {
        setLoading(true)

        try {
          const response = await client.api.get<GetCurrentUserResponse>('/api/user/me')
          setUser(response.data.data)
          window.localStorage.setItem('userData', JSON.stringify(response.data.data))
        } catch {}

        setLoading(false)
      }

      const searchParams = new URLSearchParams(location.search)
      const returnUrl = searchParams.get('returnUrl')
      const redirectURL = returnUrl && returnUrl !== '/' ? returnUrl : '/'
      navigate(redirectURL, { replace: true })
    } catch (err: any) {
      if (errorCallback) errorCallback(err)
    }
  }

  const handleLogout = () => {
    setUser(null)
    window.localStorage.removeItem('userData')
    window.localStorage.removeItem(authConfig.accessTokenKeyName)
    navigate('/login')
    setAtomUserMe({ data: {} })
    setSelectedRole(null)
    setCapabilities([])
  }

  const handleRegister = (params: RegisterParams, errorCallback?: ErrCallbackType) => {
    axios
      .post(authConfig.registerEndpoint, params)
      .then(res => {
        if (res.data.error) {
          if (errorCallback) errorCallback(res.data.error)
        } else {
          handleLogin({ email: params.email, password: params.password })
        }
      })
      .catch((err: { [key: string]: string }) => (errorCallback ? errorCallback(err) : null))
  }

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
